import {
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    OutlinedInput,
    Box,
    Avatar
} from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import MainCard from 'ui-component/cards/MainCard';
import CircularLoader from 'ui-component/CircularLoader';
import Transitions from 'ui-component/extended/Transitions';
import { useTheme, styled } from '@mui/material/styles';
import { shouldForwardProp } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import menuItems from 'menu-items';
import PropTypes from 'prop-types';
import { bindToggle } from 'material-ui-popup-state';

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 434,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important'
    },
    [theme.breakpoints.down('lg')]: {
        width: 250
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 4,
        background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff'
    }
}));

const GlobalSearch = ({ viewPort = '', popupState }) => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const [value, setValue] = useState('');
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);
    const [isSearching, setIsSearching] = useState(false);
    const [filterMenus, setFilterMenus] = useState([]);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleSearch = (value) => {
        let filterMenus = [];
        try {
            setIsSearching(true);
            setValue(value);
            setOpen(value !== '' ? true : false);
            let dynamicPattern = new RegExp(value, 'i');
            for (let menu of menuItems?.items || []) {
                // filter in menu title
                if (dynamicPattern.test(menu.title) && typeof menu?.children === 'undefined') {
                    filterMenus.push(menu);
                } else {
                    filterMenus = [...filterMenus, ...handleFilterTags(dynamicPattern, menu)];
                }
            }
        } catch (e) {
            console.log('e', e);
        } finally {
            setFilterMenus(filterMenus);
            setIsSearching(false);
        }
    };

    const handleFilterTags = (dynamicPattern, menu) => {
        const filterMenus = [];
        /**
         * Type of where can find tags
         * 1. is in the main prop
         * 2. if has children
         * 3. if has dynamicPages
         * 4. if has children and has dynamicPages
         */

        if (menu?.tags) {
            for (let tag of menu.tags) {
                // filter in tags
                if (dynamicPattern.test(tag)) {
                    filterMenus.push(menu);
                    break;
                }
            }
        }

        if (menu?.children) {
            for (let child of menu.children) {
                if (child?.dynamicPages) {
                    for (let page of child.dynamicPages) {
                        for (let tag of page?.tags || []) {
                            // filter in tags
                            if (dynamicPattern.test(tag)) {
                                page = {
                                    ...page,
                                    url: `${child?.url}${page?.urlParams ? `?page=${page?.urlParams}` : ''}`
                                };
                                filterMenus.push(page);
                                break;
                            }
                        }
                    }
                } else {
                    for (let tag of child.tags || []) {
                        // filter in tags
                        if (dynamicPattern.test(tag)) {
                            filterMenus.push(child);
                            break;
                        }
                    }
                }
            }
        }

        if (menu?.dynamicPages) {
            for (let page of menu.dynamicPages) {
                for (let tag of page.tags) {
                    // filter in tags
                    if (dynamicPattern.test(tag)) {
                        page = {
                            ...page,
                            url: `${menu?.url}${page?.urlParams ? `?page=${page?.urlParams}` : ''}`
                        };
                        filterMenus.push(page);
                        break;
                    }
                }
            }
        }
        return filterMenus;
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleNavigate = (url) => {
        window.location.href = url;
    };
    return (
        <>
            {viewPort === 'web' ? (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            ) : (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position="end">
                            <Box sx={{ ml: 2 }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        ...theme.typography.commonAvatar,
                                        ...theme.typography.mediumAvatar,
                                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.orange.light,
                                        color: theme.palette.orange.dark,
                                        '&:hover': {
                                            background: theme.palette.orange.dark,
                                            color: theme.palette.orange.light
                                        }
                                    }}
                                    {...bindToggle(popupState)}
                                >
                                    <IconX stroke={1.5} size="20px" />
                                </Avatar>
                            </Box>
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            )}
            <Popper
                placement="bottom-start"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 10]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper sx={{ width: anchorRef.current.offsetWidth }}>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column">
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                    <Grid item>
                                                        <Stack direction="row" spacing={2}>
                                                            <Typography variant="subtitle1">Search Results</Typography>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} p={0}>
                                                <Divider sx={{ my: 0 }} />
                                            </Grid>
                                            <Grid item xs={12} sx={{ py: 2 }}>
                                                <Grid container spacing={1} sx={{ px: 2 }}>
                                                    {isSearching ? (
                                                        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                            <CircularLoader size={24} minHeight="0rem" />
                                                        </Grid>
                                                    ) : filterMenus.length == 0 ? (
                                                        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                            No results
                                                        </Grid>
                                                    ) : (
                                                        <>
                                                            {filterMenus.map((menu, i) => (
                                                                <Grid item key={i}>
                                                                    <Tooltip
                                                                        placement="bottom"
                                                                        title={`Click to go to ${menu?.title || ''}`}
                                                                    >
                                                                        <Typography
                                                                            variant="body"
                                                                            sx={{
                                                                                color: '#2da8de',
                                                                                textDecoration: 'underline',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => handleNavigate(menu?.url || '')}
                                                                        >
                                                                            {menu?.title || ''}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </Grid>
                                                            ))}
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

GlobalSearch.propTypes = {
    viewPort: PropTypes.string,
    popupState: PropTypes.object
};

export default GlobalSearch;
